/**
 * Cesium side-effect manager for the path-mode panel: section line colours
 * (with hover highlight) + mast labels. Mirrors the apply/clear pattern of
 * `pathToolLoadCases.ts`.
 *
 * Originals are restored on `clearSectionStyling()` / `clearMastLabels()`,
 * so callers only need to drive these from the panel's lifecycle.
 */

import {
	Cartesian2,
	Color,
	ColorMaterialProperty,
	HeightReference,
	LabelGraphics,
	LabelStyle,
	VerticalOrigin,
	type Entity,
	type MaterialProperty
} from 'cesium';

import {
	findAllLineEntities,
	findMastEntity
} from './pathToolMapEntities';
import { abbreviateMastFunction, type EnrichedSection } from './pathToolUtils';
import { getLoadCaseLineEntitiesByLoadCaseName } from './pathToolLoadCases.svelte';
import type { ILoadCase, ISectionInfoMast } from '../map-tools-store';

// ── Section line colouring ──────────────────────────────────────────────────

const HIGHLIGHT_COLOR = Color.YELLOW.withAlpha(0.95);

// Original (pre-styling) materials keyed by entity.id, so `clearSectionStyling`
// can put them back on the exact entity we read them from. Loadcase entity ids
// include the loadcase name (e.g. `Islast line:2242746`) and can't be parsed
// back to a numeric line id, hence the entity-keyed map.
const originalLineMaterials = new Map<
	string,
	{ entity: Entity; material: MaterialProperty | undefined }
>();
// Section colour keyed by the *numeric* line id — that's the stable identity
// the Svelte handlers already have, and it survives loadcase datasource
// rebuilds (entity references change after rebuild but lineId doesn't).
// Storing the underlying `Color` (not a `MaterialProperty`) so restore creates
// a fresh `ColorMaterialProperty` on each call — Cesium's per-polyline
// visualizer cache otherwise dedupes same-reference assignments and skips the
// redraw, which used to leave the polyline stuck yellow after mouseleave.
const sectionColorByLineId = new Map<number, Color>();
// Final colour applied per entity.id — used by `restoreSectionLines` so a
// hover restore puts back whatever colour the entity actually has now (which
// can differ between plan/live originals and load-case duplicates when
// `applyLoadCaseLineColors` has overlaid loadcase rgb on top).
const finalColorByEntityId = new Map<string, Color>();

export function applySectionStyling(sections: EnrichedSection[]): void {
	clearSectionStyling();
	for (const s of sections) {
		for (const lineId of s.lineIds) {
			sectionColorByLineId.set(lineId, s.color.cesium);
			for (const entity of findAllLineEntities(lineId)) {
				if (!entity.polyline) continue;
				if (!originalLineMaterials.has(entity.id)) {
					originalLineMaterials.set(entity.id, {
						entity,
						material: entity.polyline.material
					});
				}
				entity.polyline.material = new ColorMaterialProperty(s.color.cesium);
				finalColorByEntityId.set(entity.id, s.color.cesium);
			}
		}
	}
}

/**
 * Overlays per-load-case `rgb` colours on top of the section colours. Only
 * the load-case duplicate entities are repainted — plan/live originals keep
 * the section colour they got from `applySectionStyling`. Load cases with a
 * missing or short rgb tuple are skipped (their duplicates stay section-
 * coloured). Must be called after `applySectionStyling`.
 */
export function applyLoadCaseLineColors(loadCases: ILoadCase[]): void {
	for (const lc of loadCases) {
		if (!lc.rgb || lc.rgb.length < 3) continue;
		const color = Color.fromBytes(lc.rgb[0], lc.rgb[1], lc.rgb[2], 255);
		for (const entity of getLoadCaseLineEntitiesByLoadCaseName(lc.name)) {
			if (!entity.polyline) continue;
			if (!originalLineMaterials.has(entity.id)) {
				originalLineMaterials.set(entity.id, {
					entity,
					material: entity.polyline.material
				});
			}
			entity.polyline.material = new ColorMaterialProperty(color);
			finalColorByEntityId.set(entity.id, color);
		}
	}
}

export function clearSectionStyling(): void {
	if (originalLineMaterials.size === 0) return;
	for (const { entity, material } of originalLineMaterials.values()) {
		if (entity.polyline) entity.polyline.material = material!;
	}
	originalLineMaterials.clear();
	sectionColorByLineId.clear();
	finalColorByEntityId.clear();
}

/** Hover highlight — applied across plan/live + loadcase entities. */
export function highlightSectionLines(lineIds: number[]): void {
	for (const lineId of lineIds) {
		for (const entity of findAllLineEntities(lineId)) {
			if (entity.polyline) entity.polyline.material = new ColorMaterialProperty(HIGHLIGHT_COLOR);
		}
	}
}

/**
 * Restores each entity's most recently applied colour after a hover. Reads
 * per-entity colour so load-case-coloured duplicates don't snap back to the
 * section colour when the cursor leaves; falls back to the section colour
 * when no per-entity colour was recorded (e.g. lines added after styling).
 */
export function restoreSectionLines(lineIds: number[]): void {
	for (const lineId of lineIds) {
		const sectionColor = sectionColorByLineId.get(lineId);
		for (const entity of findAllLineEntities(lineId)) {
			if (!entity.polyline) continue;
			const color = finalColorByEntityId.get(entity.id) ?? sectionColor;
			if (color) entity.polyline.material = new ColorMaterialProperty(color);
		}
	}
}

// ── Mast labels ─────────────────────────────────────────────────────────────

const MAST_LABEL_SEPARATOR = ' · ';
const labeledMasts = new Map<string, { entity: Entity; original: LabelGraphics | undefined }>();

function buildMastLabel(text: string): LabelGraphics {
	return new LabelGraphics({
		text,
		font: '600 13px Poppins, sans-serif',
		fillColor: Color.fromCssColorString('rgba(15, 22, 41, 1)'),
		style: LabelStyle.FILL,
		showBackground: true,
		backgroundColor: Color.fromCssColorString('rgba(255, 255, 255, 0.85)'),
		backgroundPadding: new Cartesian2(6, 4),
		verticalOrigin: VerticalOrigin.BOTTOM,
		pixelOffset: new Cartesian2(0, -10),
		disableDepthTestDistance: Number.POSITIVE_INFINITY,
		heightReference: HeightReference.CLAMP_TO_GROUND
	});
}

export function applyMastLabels(masts: ISectionInfoMast[]): void {
	clearMastLabels();
	for (const mast of masts) {
		const entity = findMastEntity(mast.id);
		if (!entity) continue;
		const text = [mast.operationalMarking, abbreviateMastFunction(mast.mastFunctionName)]
			.map((s) => s.trim())
			.filter(Boolean)
			.join(` ${MAST_LABEL_SEPARATOR} `);
		if (!text) continue;
		labeledMasts.set(entity.id, { entity, original: entity.label });
		entity.label = buildMastLabel(text);
	}
}

export function clearMastLabels(): void {
	if (labeledMasts.size === 0) return;
	for (const { entity, original } of labeledMasts.values()) {
		entity.label = original;
	}
	labeledMasts.clear();
}
